import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuthStore } from "@/stores/authStore";
import { isPortfolioCompleted } from "@/utils/portfolioStatus";
import { updatePortfolioName, deletePortfolio as deletePortfolioSvc, getPortfolioSize, updatePortfolioFields, createPortfolio } from "@/services/portfolio/PortfolioService";
import { fetchAllocationsForCompletion } from "@/services/portfolio/PortfolioAllocationsService";

export const usePortfolioActions = (
  portfolioId: string | undefined,
  portfolioName: string,
  portfolioSize: number,
  refetchPortfolio: () => void
) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const { user } = useAuthStore();

  const handlePortfolioNameChange = async () => {
    if (!portfolioId) return;

    try {
      await updatePortfolioName(portfolioId, portfolioName);

      refetchPortfolio();
      // Quiet success: no toast
    } catch (error) {
      console.error('Error updating portfolio name:', error);
      toast.error("Failed to update portfolio name");
    }
  };

  const handleDeletePortfolio = async () => {
    if (!portfolioId) return;

    try {
      await deletePortfolioSvc(portfolioId);

      // Invalidate portfolios query to refresh the list
      await queryClient.invalidateQueries({ queryKey: ['portfolios'] });
      
      // Quiet success: no toast
      navigate('/portfolios');
    } catch (error) {
      console.error('Error deleting portfolio:', error);
      toast.error("Failed to delete portfolio");
    }
  };

  const handleSavePortfolio = async () => {
    if (!portfolioName.trim()) {
      toast.error("Portfolio name is required");
      return;
    }

    if (portfolioId) {
      try {
        const [allocs, size] = await Promise.all([
          fetchAllocationsForCompletion(portfolioId),
          getPortfolioSize(portfolioId)
        ]);
        const safeSize = Number(size ?? portfolioSize ?? 0);
        const completed = isPortfolioCompleted({
          portfolio_size: safeSize,
          portfolio_allocations: (allocs || []) as any,
        });
        await updatePortfolioFields(portfolioId, {
          name: portfolioName,
          status: completed ? 'completed' : 'in_progress',
          updated_at: new Date().toISOString()
        });

        // Invalidate cached queries and navigate to portfolios list
        await queryClient.invalidateQueries({ queryKey: ['portfolio', portfolioId] });
        await queryClient.invalidateQueries({ queryKey: ['portfolios'] });

        // Redirect to portfolios page for clearer UX after explicit save
        navigate('/portfolios');
      } catch (error) {
        console.error('Error saving portfolio:', error);
        toast.error("Failed to save portfolio");
      }
    } else {
      setIsSaveDialogOpen(true);
    }
  };

  const handleCreateNewPortfolio = async () => {
    if (!user) {
      toast.error("Please sign in to create a portfolio");
      return;
    }

    try {
      await createPortfolio({ userId: user.id, name: portfolioName, portfolioType: 'personal' });

      // Invalidate portfolios query to refresh the list
      await queryClient.invalidateQueries({ queryKey: ['portfolios'] });
      
      // Silently redirect without showing success toast
      setIsSaveDialogOpen(false);
      navigate('/portfolios');
    } catch (error) {
      console.error('Error creating portfolio:', error);
      toast.error("Failed to create portfolio");
    }
  };

  return {
    isDeleteDialogOpen,
    setIsDeleteDialogOpen,
    isSaveDialogOpen,
    setIsSaveDialogOpen,
    handlePortfolioNameChange,
    handleDeletePortfolio,
    handleSavePortfolio,
    handleCreateNewPortfolio
  };
};
